import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';
import { Modal, Field } from '@/features/transactions/TransactionForm';
import type { ReferenceData } from '@/features/transactions/types';

type Category = {
  id: number;
  parent_id: number | null;
  name: string;
  full_path: string;
  code: string | null;
  type: string;
  behavior: string;
  is_essential: boolean;
  is_system: boolean;
  entity_id: number | null;
  is_shared: boolean;
  depth: number;
  color: string | null;
  status: string;
  selectable: boolean;
  has_children: boolean;
};

/**
 * Plano de contas — RF-CAT-01 a RF-CAT-08.
 *
 * A arvore vem achatada do servidor, ordenada por `path`: renderizar por
 * indentacao a partir de `depth` mostra a hierarquia sem montar arvore no
 * cliente, e a ordem ja e a correta por construcao.
 */
export function CategoriesPage({ selectedIds }: { selectedIds: number[] }) {
  const [editing, setEditing] = useState<Category | null>(null);
  const [creating, setCreating] = useState(false);
  const [merging, setMerging] = useState<Category | null>(null);
  const [typeFilter, setTypeFilter] = useState('');
  const queryClient = useQueryClient();

  const categories = useQuery({
    queryKey: ['categories', selectedIds, typeFilter],
    queryFn: () =>
      api.get<{ data: Category[] }>(
        `/categories${typeFilter ? `?type=${typeFilter}` : ''}`,
        { entityIds: selectedIds },
      ),
    select: (r) => r.data,
  });

  const reference = useQuery({
    queryKey: ['reference', selectedIds],
    queryFn: () => api.get<{ data: ReferenceData }>('/reference', { entityIds: selectedIds }),
    select: (r) => r.data,
    staleTime: 5 * 60 * 1000,
  });

  const remove = useMutation({
    mutationFn: (id: number) =>
      api.delete<{ message: string }>(`/categories/${id}`, { entityIds: selectedIds }),
    onSuccess: (res) => {
      window.alert(res.message);
      refresh();
    },
    onError: (e) => window.alert(e instanceof ApiError ? e.message : 'Erro ao excluir.'),
  });

  /**
   * Instala o plano de contas padrão — RF-CAT-04.
   *
   * Em produção o container sobe com `RUN_SEEDERS=false` (semear a cada
   * restart do Swarm seria pior), então a base pode existir sem categoria
   * nenhuma. Antes disto, o único conserto era abrir terminal no servidor — e
   * o sintoma que chegava ao usuário era todo seletor de categoria vazio,
   * em Lançamentos, Recorrências, Contratos e Orçamento, sem nada explicando.
   */
  const installDefaults = useMutation({
    mutationFn: () =>
      api.post<{ data: { created: number }; message: string }>('/categories/install-defaults'),
    onSuccess: (res) => {
      window.alert(res.message);
      refresh();
    },
    onError: (e) =>
      window.alert(
        e instanceof ApiError ? e.message : 'Não foi possível instalar o plano de contas.',
      ),
  });

  function refresh() {
    setCreating(false);
    setEditing(null);
    setMerging(null);
    void queryClient.invalidateQueries({ queryKey: ['categories'] });
    void queryClient.invalidateQueries({ queryKey: ['reference'] });
  }

  const list = categories.data ?? [];

  return (
    <div className="mx-auto max-w-4xl">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Plano de contas</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {list.length} categoria(s)
          </p>
        </div>
        <button
          onClick={() => setCreating(true)}
          disabled={!reference.data}
          className="rounded-lg bg-indigo-600 px-3.5 py-2 text-sm font-medium text-white transition hover:bg-indigo-700 disabled:opacity-50"
        >
          + Nova categoria
        </button>
      </div>

      <div className="mt-4 flex gap-1">
        {([['', 'Todas'], ['despesa', 'Despesas'], ['receita', 'Receitas']] as const).map(([value, label]) => (
          <button
            key={value}
            onClick={() => setTypeFilter(value)}
            className={`rounded-lg px-3 py-1.5 text-sm transition ${
              typeFilter === value
                ? 'bg-indigo-600 text-white'
                : 'border border-slate-300 text-slate-600 dark:border-slate-700 dark:text-slate-300'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {categories.isPending ? (
        <div className="mt-5 animate-pulse space-y-1.5">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="h-9 rounded bg-slate-200 dark:bg-slate-800" />
          ))}
        </div>
      ) : list.length === 0 ? (
        /*
          Lista vazia com uma caixa branca não informa nada — foi assim que o
          problema chegou: "clico em Categoria e não aparece nenhuma
          informação". O vazio precisa dizer o que é e o que fazer.
        */
        <div className="mt-4 rounded-xl border border-dashed border-slate-300 p-10 text-center dark:border-slate-700">
          {typeFilter ? (
            <>
              <p className="font-medium">
                Nenhuma categoria de {typeFilter === 'receita' ? 'receita' : 'despesa'}
              </p>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                Existem categorias cadastradas, mas nenhuma deste tipo. Troque o filtro acima ou
                crie uma.
              </p>
            </>
          ) : (
            <>
              <p className="font-medium">Nenhuma categoria cadastrada</p>
              <p className="mx-auto mt-1 max-w-lg text-sm text-slate-500 dark:text-slate-400">
                Sem plano de contas, o campo <strong>Categoria</strong> aparece vazio em
                Lançamentos, Recorrências, Contratos e Orçamento — e o relatório por categoria
                não tem o que agrupar.
              </p>

              <button
                onClick={() => {
                  if (
                    window.confirm(
                      'Instalar o plano de contas padrão?\n\n' +
                        'Cria as árvores empresarial e pessoal (receitas, custos, despesas ' +
                        'operacionais, pessoais e financeiras), compartilhadas entre todas as ' +
                        'entidades.\n\n' +
                        'Nada é sobrescrito: se já existir qualquer categoria, a operação é ' +
                        'recusada. Depois, tudo pode ser renomeado, desativado ou complementado.',
                    )
                  ) {
                    installDefaults.mutate();
                  }
                }}
                disabled={installDefaults.isPending}
                className="mt-4 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-indigo-700 disabled:opacity-50"
              >
                {installDefaults.isPending ? 'Instalando…' : 'Instalar plano de contas padrão'}
              </button>

              <p className="mt-3 text-xs text-slate-400">
                ou <strong>+ Nova categoria</strong>, se preferir montar do zero
              </p>
            </>
          )}
        </div>
      ) : (
        <div className="mt-4 rounded-xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
          {list.map((c) => (
            <div
              key={c.id}
              className="flex items-center gap-2 border-b border-slate-100 px-3 py-2 text-sm last:border-0 dark:border-slate-800"
              style={{ paddingLeft: `${0.75 + c.depth * 1.25}rem` }}
            >
              {c.depth > 0 && <span className="text-slate-300 dark:text-slate-600">└</span>}

              <span className={c.status === 'inativa' ? 'text-slate-400 line-through' : ''}>
                {c.name}
              </span>

              {c.code && <span className="text-xs text-slate-400">{c.code}</span>}

              <span
                className={`rounded px-1.5 py-0.5 text-[10px] font-medium uppercase ${
                  c.type === 'receita'
                    ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300'
                    : c.type === 'despesa'
                      ? 'bg-rose-100 text-rose-700 dark:bg-rose-950 dark:text-rose-300'
                      : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400'
                }`}
              >
                {c.type}
              </span>

              {/* RN-91 depende disto: só categoria variável recebe tendência linear. */}
              {c.behavior === 'fixa' && (
                <span className="text-[10px] uppercase tracking-wide text-slate-400">fixa</span>
              )}
              {c.is_essential && (
                <span className="text-[10px] uppercase tracking-wide text-amber-600 dark:text-amber-400">
                  essencial
                </span>
              )}
              {!c.is_shared && (
                <span className="text-[10px] uppercase tracking-wide text-indigo-500">
                  exclusiva
                </span>
              )}
              {c.is_system && (
                <span className="text-[10px] uppercase tracking-wide text-slate-400">sistema</span>
              )}

              <div className="ml-auto flex gap-2.5 text-[11px]">
                {!c.is_system && (
                  <>
                    <button
                      onClick={() => setEditing(c)}
                      className="text-slate-500 hover:underline dark:text-slate-400"
                    >
                      editar
                    </button>
                    <button
                      onClick={() => setMerging(c)}
                      className="text-slate-500 hover:underline dark:text-slate-400"
                      title="Mover os lançamentos para outra categoria e remover esta"
                    >
                      mesclar
                    </button>
                    <button
                      onClick={() => {
                        if (window.confirm(
                          `Excluir "${c.name}"?\n\n` +
                          'Se houver lançamentos, ela será inativada em vez de excluída — ' +
                          'os relatórios de períodos passados continuam corretos.',
                        )) {
                          remove.mutate(c.id);
                        }
                      }}
                      className="text-rose-600 hover:underline dark:text-rose-400"
                    >
                      excluir
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <p className="mt-3 text-[11px] text-slate-400">
        Categoria com subcategorias é agrupador: os lançamentos vão nas folhas. Orçar a categoria-pai
        soma toda a subárvore.
      </p>

      {(creating || editing) && reference.data && (
        <CategoryForm
          category={editing}
          categories={list}
          reference={reference.data}
          selectedIds={selectedIds}
          onClose={() => { setCreating(false); setEditing(null); }}
          onSaved={refresh}
        />
      )}

      {merging && (
        <MergeDialog
          source={merging}
          categories={list}
          selectedIds={selectedIds}
          onClose={() => setMerging(null)}
          onMerged={refresh}
        />
      )}
    </div>
  );
}

// -------------------------------------------------------------------

function CategoryForm({
  category, categories, reference, selectedIds, onClose, onSaved,
}: {
  category: Category | null;
  categories: Category[];
  reference: ReferenceData;
  selectedIds: number[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEdit = category !== null;

  const [name, setName] = useState(category?.name ?? '');
  const [code, setCode] = useState(category?.code ?? '');
  const [type, setType] = useState(category?.type ?? 'despesa');
  const [parentId, setParentId] = useState(String(category?.parent_id ?? ''));
  const [entityId, setEntityId] = useState(String(category?.entity_id ?? ''));
  const [behavior, setBehavior] = useState(category?.behavior ?? 'variavel');
  const [isEssential, setIsEssential] = useState(category?.is_essential ?? false);

  // Um pai dentro da própria subárvore criaria um ciclo. O backend recusa;
  // aqui a opção nem aparece.
  const possibleParents = categories.filter(
    (c) => !c.is_system && c.id !== category?.id && (c.type === type || c.type === 'ambos'),
  );

  const save = useMutation({
    mutationFn: () => {
      const payload = {
        name,
        ...(code ? { code } : {}),
        type,
        parent_id: parentId ? Number(parentId) : null,
        behavior,
        is_essential: isEssential,
        ...(isEdit ? {} : { entity_id: entityId ? Number(entityId) : null }),
      };

      return isEdit
        ? api.patch(`/categories/${category.id}`, payload, { entityIds: selectedIds })
        : api.post('/categories', payload, { entityIds: selectedIds });
    },
    onSuccess: (res) => {
      const warning = (res as { meta?: { warning?: string | null } })?.meta?.warning;
      if (warning) window.alert(warning);
      onSaved();
    },
  });

  const error = save.error instanceof ApiError ? save.error : null;

  return (
    <Modal title={isEdit ? 'Editar categoria' : 'Nova categoria'} onClose={onClose}>
      <form onSubmit={(e) => { e.preventDefault(); save.mutate(); }} className="space-y-4">
        {error && (
          <div
            role="alert"
            className="rounded-lg border border-rose-200 bg-rose-50 px-3.5 py-2.5 text-sm text-rose-800 dark:border-rose-900/60 dark:bg-rose-950/40 dark:text-rose-300"
          >
            <p>{error.message}</p>
            {error.errors && (
              <ul className="mt-1 list-inside list-disc">
                {Object.values(error.errors).map((m, i) => <li key={i}>{m[0]}</li>)}
              </ul>
            )}
          </div>
        )}

        <Field label="Nome" required>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            minLength={2}
            autoFocus
            className="input w-full"
          />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Tipo" required>
            <select
              value={type}
              onChange={(e) => { setType(e.target.value); setParentId(''); }}
              className="input w-full"
            >
              <option value="despesa">Despesa</option>
              <option value="receita">Receita</option>
              <option value="ambos">Ambos</option>
            </select>
          </Field>
          <Field label="Código" hint="opcional, ex.: 3.1.02">
            <input value={code} onChange={(e) => setCode(e.target.value)} className="input w-full" />
          </Field>
        </div>

        <Field label="Dentro de" hint="deixe vazio para categoria raiz">
          <select
            value={parentId}
            onChange={(e) => setParentId(e.target.value)}
            className="input w-full"
          >
            <option value="">— raiz —</option>
            {possibleParents.map((c) => (
              <option key={c.id} value={c.id}>{c.full_path}</option>
            ))}
          </select>
        </Field>

        {!isEdit && (
          <Field label="Escopo" hint="compartilhada aparece para todas as entidades">
            <select
              value={entityId}
              onChange={(e) => setEntityId(e.target.value)}
              className="input w-full"
            >
              <option value="">Compartilhada</option>
              {reference.entities.map((e) => (
                <option key={e.id} value={e.id}>Exclusiva de {e.name}</option>
              ))}
            </select>
          </Field>
        )}

        {/* Os dois campos que parecem cadastro e dirigem cálculo. */}
        <div className="rounded-lg border border-slate-200 p-3 dark:border-slate-800">
          <Field label="Comportamento" required>
            <select
              value={behavior}
              onChange={(e) => setBehavior(e.target.value)}
              className="input w-full"
            >
              <option value="variavel">Variável — muda todo mês (mercado, combustível)</option>
              <option value="fixa">Fixa — mesmo valor todo mês (aluguel, assinatura)</option>
            </select>
          </Field>
          <p className="mt-1.5 text-xs text-slate-500 dark:text-slate-400">
            O orçamento só projeta tendência pela média diária em categoria <strong>variável</strong>.
            Extrapolar o aluguel diria que ele custa cinco vezes mais no fim do mês.
          </p>

          <label className="mt-3 flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              checked={isEssential}
              onChange={(e) => setIsEssential(e.target.checked)}
              className="mt-0.5"
            />
            <span>
              Despesa essencial
              <span className="block text-xs text-slate-500 dark:text-slate-400">
                Entra na priorização quando o caixa não cobre tudo.
              </span>
            </span>
          </label>
        </div>

        <div className="flex gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 rounded-lg border border-slate-300 px-4 py-2.5 text-sm font-medium dark:border-slate-700"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={name.trim().length < 2 || save.isPending}
            className="flex-1 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-indigo-700 disabled:opacity-50"
          >
            {save.isPending ? 'Salvando…' : 'Salvar'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function MergeDialog({
  source, categories, selectedIds, onClose, onMerged,
}: {
  source: Category;
  categories: Category[];
  selectedIds: number[];
  onClose: () => void;
  onMerged: () => void;
}) {
  const [targetId, setTargetId] = useState('');

  const merge = useMutation({
    mutationFn: () =>
      api.post<{ meta: { message: string } }>(
        `/categories/${source.id}/merge`,
        { target_id: Number(targetId) },
        { entityIds: selectedIds },
      ),
    onSuccess: (res) => {
      window.alert(res.meta.message);
      onMerged();
    },
  });

  const error = merge.error instanceof ApiError ? merge.error : null;

  const targets = categories.filter(
    (c) => c.id !== source.id && !c.is_system && c.type === source.type,
  );

  return (
    <Modal title={`Mesclar "${source.name}"`} onClose={onClose}>
      <div className="space-y-4">
        {error && (
          <div
            role="alert"
            className="rounded-lg border border-rose-200 bg-rose-50 px-3.5 py-2.5 text-sm text-rose-800 dark:border-rose-900/60 dark:bg-rose-950/40 dark:text-rose-300"
          >
            {error.message}
          </div>
        )}

        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3.5 py-3 text-sm dark:border-amber-900/50 dark:bg-amber-950/30">
          <p className="font-medium text-amber-900 dark:text-amber-200">Isto reclassifica o passado.</p>
          <p className="mt-1 text-amber-800/80 dark:text-amber-300/80">
            Todos os lançamentos de <strong>{source.name}</strong> passam para a categoria escolhida,
            inclusive os de meses já fechados. Os relatórios daqueles períodos vão somar os dois
            juntos a partir de agora. As subcategorias são adotadas pelo destino.
          </p>
        </div>

        <Field label="Mover tudo para" required>
          <select
            value={targetId}
            onChange={(e) => setTargetId(e.target.value)}
            className="input w-full"
          >
            <option value="">Selecione</option>
            {targets.map((c) => <option key={c.id} value={c.id}>{c.full_path}</option>)}
          </select>
        </Field>

        <div className="flex gap-2">
          <button
            onClick={onClose}
            className="flex-1 rounded-lg border border-slate-300 px-4 py-2.5 text-sm font-medium dark:border-slate-700"
          >
            Cancelar
          </button>
          <button
            onClick={() => merge.mutate()}
            disabled={targetId === '' || merge.isPending}
            className="flex-1 rounded-lg bg-amber-600 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-amber-700 disabled:opacity-50"
          >
            {merge.isPending ? 'Mesclando…' : 'Confirmar mesclagem'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
